import type { CRMUser, DesignNodeData } from '../types';
import type { PayrollPosition, PayrollSettings } from '../types/finances';
import { listDesigners } from './dealDesignerArchitect';
import {
  buildInstallLaborPeople,
  type InstallLaborPerson,
} from './proposalInstallLabor';

const WORK_HOURS_PER_DAY = 8;
export const DEFAULT_LEAD_DESIGNER_RATE = 120;

export type JoineryPerson = {
  id: string;
  name: string;
  title?: string;
  userId: string;
  hourlyRate: number | null;
};

export type JoineryLineKey = 'pm' | 'apm' | 'lead' | 'support';

export function joineryHourlyFromDayRate(dayRate: number): number | null {
  if (!(dayRate > 0)) return null;
  return Math.round((dayRate / WORK_HOURS_PER_DAY) * 100) / 100;
}

export function joineryDesignerRate(
  user: Pick<CRMUser, 'designerHourlyRate'> | null | undefined,
  fallback = DEFAULT_LEAD_DESIGNER_RATE,
): number {
  const n = Number(user?.designerHourlyRate);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

export function staffToJoineryPeople(people: InstallLaborPerson[]): JoineryPerson[] {
  return people
    .filter((person) => !!person.userId)
    .map((person) => ({
      id: person.userId as string,
      name: person.name,
      title: person.title,
      userId: person.userId as string,
      hourlyRate: joineryHourlyFromDayRate(person.ratePerDay || 0),
    }));
}

export function designersToJoineryPeople(
  crmUsers: CRMUser[],
  ratesByUserId?: Record<string, number>,
): JoineryPerson[] {
  return listDesigners(crmUsers).map((user) => {
    const fromMap = ratesByUserId?.[user.uid];
    const fromProfile = user.designerHourlyRate;
    const hourlyRate =
      fromMap != null && fromMap > 0
        ? fromMap
        : fromProfile != null && fromProfile > 0
          ? fromProfile
          : null;
    return {
      id: user.uid,
      name: (user.displayName || user.email || '').trim() || user.uid,
      title: (user.title || '').trim() || undefined,
      userId: user.uid,
      hourlyRate,
    };
  });
}

export function buildJoineryStaffPeople(
  crmUsers: CRMUser[],
  positions: PayrollPosition[],
  settings: PayrollSettings,
): JoineryPerson[] {
  return staffToJoineryPeople(buildInstallLaborPeople(crmUsers, positions, settings));
}

export function joineryPeopleForLine(
  key: JoineryLineKey,
  staff: JoineryPerson[],
  designers: JoineryPerson[],
): JoineryPerson[] {
  return key === 'lead' || key === 'support' ? designers : staff;
}

export function matchJoineryPerson(
  people: JoineryPerson[],
  userId?: string,
): JoineryPerson | undefined {
  if (!userId) return undefined;
  return people.find((person) => person.userId === userId);
}

/** Seed the project designer onto the lead line once, using their profile rate. */
export function withAssignedLeadDesigner(
  design: DesignNodeData,
  assigned: { uid: string } | null | undefined,
  designer: Pick<CRMUser, 'designerHourlyRate'> | null | undefined,
  fallbackRate = DEFAULT_LEAD_DESIGNER_RATE,
): DesignNodeData {
  if (design.leadDesignerUserId || !assigned?.uid) return design;
  return {
    ...design,
    leadDesignerUserId: assigned.uid,
    leadDesignerRate: joineryDesignerRate(designer, fallbackRate),
  };
}
